import { memo, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { longDate } from '../../shared/taxonomy';
import { localDate, startMinute, type Opportunity } from '../../shared/opportunity';
import { attendedSpan, type Trims } from '../schedule/attendance';
import type { LegChoices } from '../schedule/travelLegs';
import type { HomeStore } from '../schedule/useHome';
import DayDetail from '../schedule/DayDetail';
import MapMyDay from '../schedule/MapMyDay';
import { useHovered, type HoverStore } from './useHoverPreview';

/**
 * Explore's right sidebar with no callout open: the day the list is on, and how
 * the user's schedule for it hangs together. The day follows the list as it
 * scrolls (`EventList`'s `onFocusDay`).
 *
 * The date, then the day at a glance (the mini day, holding only what is
 * scheduled), then MAP MY DAY. Everything in it opens in the callout.
 *
 * Hovering a card in the list previews it here, in its own day: a ghost in the
 * mini day, with whatever it would clash with, and a stop on the route. The
 * pointer leaving puts the scrolled-to day back.
 */

/**
 * The hour labels hang 44px left of the sky, past the sidebar's 32px padding:
 * everything here sits 12px further in, so the date, the sky and the map share
 * one left edge, as in the reference.
 */
const Body = styled('div')({
  paddingLeft: 12,
});

const DateHeading = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  margin: '6px 0 14px',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 20,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  color: BRAND.parchment,
  '& > span': { fontWeight: 400, fontSize: 17, letterSpacing: 0 },
});

const Empty = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.7)',
});

/** The reference's mini day: about 39px an hour, and a window of about 420px. */
const HOUR_PX = 39;
const WINDOW_HOURS = 420 / HOUR_PX;
/** Room kept before the first event and after the last. */
const MARGIN_HOURS = 2;
/** A hovered event this close to the window's edge is outside it. */
const EDGE_MINUTES = 30;

const NO_IDS = new Set<string>();

const isTimed = (e: Opportunity) => !e.isAllDay && startMinute(e) != null;

interface Props {
  /** The day the list is on. */
  dayKey: string;
  /** Everything on the schedule; the day's share is picked out here. */
  scheduled: Opportunity[];
  trims: Trims;
  legChoices: LegChoices;
  home: HomeStore;
  onOpenEvent: (event: Opportunity) => void;
  /** The card under the pointer. */
  hover: HoverStore;
  /** A click on the mini day, off its events: this day on My Schedule. */
  onOpenDay: (dayKey: string, minute: number) => void;
  /** The mini day's ✕. */
  onRemove: (id: string) => void;
}

function DayFocus({ dayKey, scheduled, trims, legChoices, home, onOpenEvent, hover, onOpenDay, onRemove }: Props) {
  const preview = useHovered(hover);
  // Where a hovered card would go is answered in its own day.
  const day = preview ? localDate(preview) : dayKey;
  const events = useMemo(() => scheduled.filter((e) => localDate(e) === day), [scheduled, day]);
  const ghost = preview && isTimed(preview) ? preview : null;

  /**
   * The window: tall enough for the day and two hours either side, centred on
   * it. A hovered event already inside it leaves it where it is (as the map
   * keeps its frame); one outside it slides it along to centre that event, and
   * the pointer leaving slides it back.
   */
  const daySpan = useMemo(() => {
    const spans = events.filter(isTimed).map((e) => attendedSpan(e, trims));
    return spans.length
      ? { start: Math.min(...spans.map((s) => s.start)), end: Math.max(...spans.map((s) => s.end)) }
      : null;
  }, [events, trims]);
  const hours = Math.max(WINDOW_HOURS, daySpan ? (daySpan.end - daySpan.start) / 60 + 2 * MARGIN_HOURS : 0);
  const rest = daySpan ? (daySpan.start + daySpan.end) / 2 : 9.5 * 60;

  const [view, setView] = useState({ day, rest, centre: rest });
  let next = view.day === day && view.rest === rest ? view : { day, rest, centre: rest };
  if (ghost) {
    const span = attendedSpan(ghost, trims);
    const half = hours * 30;
    const inside = span.start >= next.centre - half + EDGE_MINUTES && span.end <= next.centre + half - EDGE_MINUTES;
    const centre = (span.start + span.end) / 2;
    // An event too long for the window is never inside it, even centred on it:
    // move only when the centre changes, or this sets state on every render.
    if (!inside && next.centre !== centre) next = { ...next, centre };
  } else if (next.centre !== rest) {
    next = { ...next, centre: rest };
  }
  if (next !== view) setView(next);

  const { full, weekday } = longDate(day);

  return (
    <Body>
      <DateHeading>
        {full}
        <span>{weekday}</span>
      </DateHeading>

      {events.some(isTimed) || ghost ? (
        <DayDetail
          // A new day is a new window, placed rather than slid to.
          key={day}
          mini
          dayKey={day}
          events={events}
          previewEvent={ghost}
          savedIds={NO_IDS}
          colorBy="none"
          trims={trims}
          legChoices={legChoices}
          hourPx={HOUR_PX}
          windowHours={hours}
          windowCentre={next.centre}
          glide
          home={home.home}
          onOpenEvent={onOpenEvent}
          onRemove={onRemove}
          onOpenDay={onOpenDay}
        />
      ) : (
        <Empty>
          {events.length
            ? `Nothing with a set time on your schedule for ${weekday}.`
            : `Nothing on your schedule for ${weekday} yet. Add events from the list and they appear here, with the route between them.`}
        </Empty>
      )}

      <MapMyDay events={events} preview={preview} trims={trims} home={home} onOpenEvent={onOpenEvent} />
    </Body>
  );
}

// The page redraws for every page of events that arrives; this day need not.
export default memo(DayFocus);
